import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../components/layout/DashboardLayout';
import usePageTitle from '../hooks/usePageTitle';
import useSmartRefresh from '../hooks/useSmartRefresh';
import subscriptionService from '../services/subscriptionService';
import { toast } from 'react-hot-toast';

/**
 * Subscription Page - Manage subscription plans and billing
 */
const SubscriptionPage = () => {
  // Set page title
  usePageTitle('Subscription');

  // Utility function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Data fetching function
  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      
      // Fetch current subscription and available plans
      const [subscriptionResponse, plansResponse] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getPlans()
      ]);

      if (subscriptionResponse.success && subscriptionResponse.subscription) {
        setCurrentSubscription(subscriptionResponse.subscription);
      }

      if (plansResponse.success && plansResponse.plans) {
        setPlans(plansResponse.plans);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  useSmartRefresh(
    fetchSubscriptionData,
    'Subscription data refreshed successfully'
  );

  // Load data on component mount
  useEffect(() => {
    fetchSubscriptionData();
  }, []);



  const handleRenew = () => {
    setShowRenewModal(true);
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmUpgrade = async () => {
    if (!selectedPlan) return;
    
    try {
      setActionLoading(true);
      const response = await subscriptionService.upgradeSubscription(selectedPlan.id);
      
      if (response.success) {
        toast.success(`Successfully upgraded to ${selectedPlan.name}`);
        setShowUpgradeModal(false);
        setSelectedPlan(null);
        await fetchSubscriptionData(); // Refresh data
      } else {
        toast.error(response.message || 'Failed to upgrade subscription');
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast.error('Failed to upgrade subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmRenew = async () => {
    try {
      setActionLoading(true);
      const response = await subscriptionService.renewSubscription();
      
      if (response.success) {
        toast.success('Subscription renewed successfully');
        setShowRenewModal(false);
        await fetchSubscriptionData(); // Refresh data
      } else {
        toast.error(response.message || 'Failed to renew subscription');
      }
    } catch (error) {
      console.error('Error renewing subscription:', error);
      toast.error('Failed to renew subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmCancel = async () => {
    try {
      setActionLoading(true);
      const response = await subscriptionService.cancelSubscription();
      
      if (response.success) {
        toast.success('Subscription cancelled successfully');
        setShowCancelModal(false);
        await fetchSubscriptionData(); // Refresh data
      } else {
        toast.error(response.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    // Check if user is trying to select current plan
    if (currentSubscription && currentSubscription.plan === plan.name) {
      toast.info('You are already subscribed to this plan');
      return;
    }

    // If user has no active subscription, subscribe directly
    if (!currentSubscription || currentSubscription.status !== 'active') {
      try {
        setActionLoading(true);
        const response = await subscriptionService.subscribeToPlan(plan.id);
        
        if (response.success) {
          toast.success(`Successfully subscribed to ${plan.name}`);
          await fetchSubscriptionData(); // Refresh data
        } else {
          toast.error(response.message || 'Failed to subscribe to plan');
        }
      } catch (error) {
        console.error('Error subscribing to plan:', error);
        toast.error('Failed to subscribe to plan');
      } finally {
        setActionLoading(false);
      }
      return;
    }

    // User has active subscription - handle plan switching
    const currentPlanPrice = parseFloat(currentSubscription.price);
    const newPlanPrice = parseFloat(plan.price);
    
    if (newPlanPrice > currentPlanPrice) {
      // Upgrading to higher tier - show upgrade modal
      setSelectedPlan(plan);
      setShowUpgradeModal(true);
    } else if (newPlanPrice < currentPlanPrice) {
      // Downgrading to lower tier - show confirmation
      toast((t) => (
        <div className="flex flex-col space-y-2">
          <span className="font-medium">Switch to {plan.name}?</span>
          <span className="text-sm text-gray-600">
            You'll be downgrading from ${currentPlanPrice} to ${newPlanPrice}/month. 
            Changes will take effect at your next billing cycle.
          </span>
          <div className="flex space-x-2 mt-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                confirmPlanSwitch(plan);
              }}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Confirm Switch
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      ), {
        duration: 8000,
        position: 'top-center'
      });
    } else {
      // Same price tier - direct switch
      confirmPlanSwitch(plan);
    }
  };

  const confirmPlanSwitch = async (plan) => {
    try {
      setActionLoading(true);
      const response = await subscriptionService.subscribeToPlan(plan.id);
      
      if (response.success) {
        toast.success(`Successfully switched to ${plan.name}`);
        await fetchSubscriptionData(); // Refresh data
      } else {
        toast.error(response.message || 'Failed to switch plan');
      }
    } catch (error) {
      console.error('Error switching plan:', error);
      toast.error('Failed to switch plan');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success-600 dark:text-success-400 bg-success-100 dark:bg-success-900/30';
      case 'expired':
        return 'text-error-600 dark:text-error-400 bg-error-100 dark:bg-error-900/30';
      case 'expiring':
        return 'text-warning-600 dark:text-warning-400 bg-warning-100 dark:bg-warning-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800';
    }
  };

  return (
    <DashboardLayout pageName="Subscription">
      <div className="space-y-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Manage your subscription plan and billing information
          </p>
        </motion.div>

        {/* Current Subscription Card */}
        {currentSubscription && (
          <motion.div
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Current Subscription</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentSubscription.status)}`}>
                {currentSubscription.status.charAt(0).toUpperCase() + currentSubscription.status.slice(1)}
              </span>
            </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plan Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{currentSubscription.plan}</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Monthly Price:</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">${currentSubscription.price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Start Date:</span>
                  <span className="text-gray-900 dark:text-gray-100">{formatDate(currentSubscription.startDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">End Date:</span>
                  <span className="text-gray-900 dark:text-gray-100">{formatDate(currentSubscription.endDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Next Billing:</span>
                  <span className="text-gray-900 dark:text-gray-100">{formatDate(currentSubscription.nextBilling)}</span>
                </div>
              </div>
            </div>

            {/* Usage Statistics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Usage This Month</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600 dark:text-gray-400">IMEI Checks</span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {currentSubscription.totalChecks - currentSubscription.checksRemaining} / {currentSubscription.totalChecks}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((currentSubscription.totalChecks - currentSubscription.checksRemaining) / currentSubscription.totalChecks) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {currentSubscription.checksRemaining} checks remaining
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleRenew}
              disabled={actionLoading}
              className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
            >
              {actionLoading ? 'Processing...' : 'Renew Subscription'}
            </button>
            <button
              onClick={handleCancel}
              disabled={actionLoading}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
            >
              {actionLoading ? 'Processing...' : 'Cancel Subscription'}
            </button>
          </div>
        </motion.div>
        )}

        {/* No Subscription Message */}
        {!currentSubscription && !loading && (
          <motion.div
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">No Active Subscription</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Choose a subscription plan below to start using our IMEI checking services.
            </p>
          </motion.div>
        )}

        {/* Loading State */}
        {loading && (
          <motion.div
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                    <div className="space-y-2">
                      {[1, 2, 3, 4].map((j) => (
                        <div key={j} className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Available Plans */}
        {!loading && plans.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Available Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan, index) => (
              <motion.div
                key={plan.id || index}
                className={`relative bg-white dark:bg-gray-900 rounded-2xl p-6 border-2 transition-all duration-300 ${
                  plan.popular 
                    ? 'border-primary-500 shadow-lg' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">{plan.name}</h3>
                  <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                    ${plan.price}
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">per month</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Up to {(plan.checks || plan.imei_checks_limit || 0).toLocaleString()} checks
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  {(plan.features || []).map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <svg className="w-5 h-5 text-success-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-400 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={actionLoading || (currentSubscription && currentSubscription.plan === plan.name)}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                    currentSubscription && currentSubscription.plan === plan.name
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : plan.popular
                      ? 'bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {actionLoading ? 'Processing...' : 
                   currentSubscription && currentSubscription.plan === plan.name ? 'Current Plan' : 'Select Plan'}
                </button>
              </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Features Comparison */}
        <motion.div
          className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Plan Features</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Feature</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Basic</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Professional</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Monthly IMEI Checks</td>
                  <td className="py-3 px-4 text-center text-gray-900 dark:text-gray-100">100</td>
                  <td className="py-3 px-4 text-center text-gray-900 dark:text-gray-100">1,000</td>
                  <td className="py-3 px-4 text-center text-gray-900 dark:text-gray-100">10,000</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 px-4 text-gray-600 dark:text-gray-400">API Access</td>
                  <td className="py-3 px-4 text-center">❌</td>
                  <td className="py-3 px-4 text-center">✅</td>
                  <td className="py-3 px-4 text-center">✅</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Priority Support</td>
                  <td className="py-3 px-4 text-center">❌</td>
                  <td className="py-3 px-4 text-center">✅</td>
                  <td className="py-3 px-4 text-center">✅</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Custom Integrations</td>
                  <td className="py-3 px-4 text-center">❌</td>
                  <td className="py-3 px-4 text-center">❌</td>
                  <td className="py-3 px-4 text-center">✅</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-600 dark:text-gray-400">Dedicated Account Manager</td>
                  <td className="py-3 px-4 text-center">❌</td>
                  <td className="py-3 px-4 text-center">❌</td>
                  <td className="py-3 px-4 text-center">✅</td>
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {showUpgradeModal && selectedPlan && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)} />
            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Upgrade to {selectedPlan.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to upgrade to the {selectedPlan.name} plan for ${selectedPlan.price}/month?
                  </p>
                  <div className="flex space-x-4">
                    <button
                      onClick={confirmUpgrade}
                      disabled={actionLoading}
                      className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                      {actionLoading ? 'Processing...' : 'Confirm Upgrade'}
                    </button>
                    <button
                      onClick={() => setShowUpgradeModal(false)}
                      className="flex-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Renew Modal */}
      <AnimatePresence>
        {showRenewModal && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm" onClick={() => setShowRenewModal(false)} />
            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Renew Subscription
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Renew your {currentSubscription.plan} plan for another month at ${currentSubscription.price}?
                  </p>
                  <div className="flex space-x-4">
                    <button
                      onClick={confirmRenew}
                      disabled={actionLoading}
                      className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                      {actionLoading ? 'Processing...' : 'Renew Now'}
                    </button>
                    <button
                      onClick={() => setShowRenewModal(false)}
                      className="flex-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancel Modal */}
      <AnimatePresence>
        {showCancelModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Cancel Subscription
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to cancel your {currentSubscription?.plan} subscription? You will lose access to premium features at the end of your current billing period.
                  </p>
                  <div className="flex space-x-4">
                    <button
                      onClick={confirmCancel}
                      disabled={actionLoading}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                      {actionLoading ? 'Processing...' : 'Confirm Cancel'}
                    </button>
                    <button
                      onClick={() => setShowCancelModal(false)}
                      className="flex-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                      Keep Subscription
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
};

export default SubscriptionPage;